import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { firebaseStorage, firestore, isFirebaseConfigured } from "@/lib/firebase";

const ordersCollection = () => collection(firestore, "orders");

function normalizeDoc(snapshot) {
  const data = snapshot.data();
  const created = data.createdAt?.toDate ? data.createdAt.toDate().toISOString() : data.created_at || new Date().toISOString();
  return {
    order_id: data.orderId || snapshot.id,
    app_id: data.app_id || data.appId || "premium_1_year",
    app_title: data.app_title || data.appTitle || "MyanTax Box Premium",
    user_id: data.user_id || data.userId,
    user_email: data.user_email || data.userEmail,
    user_name: data.user_name || data.userName,
    payment_method: data.payment_method,
    amount_mmk: data.amount_mmk || data.amount,
    screenshot_url: data.screenshot_url || data.paymentProofUrl,
    screenshot_path: data.screenshot_path,
    status: data.status || "pending",
    created_at: created,
    reviewed_at: data.reviewed_at || (data.approvedAt?.toDate ? data.approvedAt.toDate().toISOString() : null),
  };
}

export async function createFirebaseOrder({ app, user, paymentMethod, screenshot }) {
  if (!isFirebaseConfigured) throw new Error("Firebase is not configured");
  if (!user?.user_id) throw new Error("Please login before ordering");

  const draft = await addDoc(ordersCollection(), {
    orderId: "",
    userId: user.user_id,
    userEmail: user.email,
    userName: user.name,
    paymentProofUrl: "",
    amount: app.price_mmk,
    currency: "MMK",
    planDuration: "1_year",
    app_id: app.app_id,
    app_title: app.title,
    user_id: user.user_id,
    user_email: user.email,
    user_name: user.name,
    payment_method: paymentMethod,
    amount_mmk: app.price_mmk,
    status: "pending",
    source: "firebase",
    createdAt: serverTimestamp(),
  });

  const extension = screenshot.name?.split(".").pop() || "png";
  const screenshotPath = `payment-screenshots/${user.user_id}/${draft.id}.${extension}`;
  const screenshotRef = ref(firebaseStorage, screenshotPath);
  await uploadBytes(screenshotRef, screenshot, { contentType: screenshot.type || "image/png" });
  const screenshotUrl = await getDownloadURL(screenshotRef);

  await updateDoc(doc(firestore, "orders", draft.id), {
    orderId: draft.id,
    paymentProofUrl: screenshotUrl,
    screenshot_path: screenshotPath,
    screenshot_url: screenshotUrl,
    updatedAt: serverTimestamp(),
  });

  return draft.id;
}

/**
 * Check if a user already has a pending order for a specific app.
 * Used to prevent duplicate orders.
 */
export async function hasUserPendingOrderForApp(userId, appId) {
  if (!isFirebaseConfigured) throw new Error("Firebase is not configured");
  try {
    const q = query(
      ordersCollection(),
      where("user_id", "==", userId),
      where("status", "==", "pending"),
      where("app_id", "==", appId),
    );
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  } catch {
    // If the query fails (e.g. missing index), allow the order through
    return false;
  }
}

export async function listFirebaseUserOrders(userId) {
  if (!isFirebaseConfigured) throw new Error("Firebase is not configured");
  const result = await getDocs(query(ordersCollection(), where("user_id", "==", userId)));
  return result.docs.map(normalizeDoc).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
}

export async function listFirebaseOrders() {
  if (!isFirebaseConfigured) throw new Error("Firebase is not configured");
  const result = await getDocs(ordersCollection());
  return result.docs.map(normalizeDoc).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
}

export async function updateFirebaseOrderStatus(orderId, status) {
  if (!isFirebaseConfigured) throw new Error("Firebase is not configured");
  const orderRef = doc(firestore, "orders", orderId);
  await updateDoc(orderRef, {
    status,
    reviewed_at: new Date().toISOString(),
    approvedAt: status === "approved" ? Timestamp.fromDate(new Date()) : null,
    updatedAt: serverTimestamp(),
  });
  if (status === "approved") {
    const orderSnapshot = await getDoc(orderRef);
    if (orderSnapshot.exists()) {
      const order = orderSnapshot.data();
      const approvedAt = new Date();
      const expiresAt = new Date();
      expiresAt.setFullYear(expiresAt.getFullYear() + 1);
      const userId = order.user_id || order.userId;
      const appId = order.app_id || order.appId || "premium_1_year";
      await setDoc(doc(firestore, "users", userId), {
        premium: true,
        premium_expires_at: expiresAt.toISOString(),
        plan: "premium",
        subscriptionStatus: "active",
        premiumStart: Timestamp.fromDate(approvedAt),
        premiumExpiry: Timestamp.fromDate(expiresAt),
        maxDevices: 2,
        purchases: {
          [appId]: {
            status: "active",
            order_id: orderId,
            approved_at: approvedAt.toISOString(),
            expires_at: expiresAt.toISOString(),
          },
        },
        updatedAt: serverTimestamp(),
      }, { merge: true });
    }
  }
}

export async function grantFirebaseGift({ email, app, adminUser }) {
  if (!isFirebaseConfigured) throw new Error("Firebase is not configured");
  const normalizedEmail = email.trim().toLowerCase();
  if (!normalizedEmail) throw new Error("User email is required");
  if (!app?.app_id) throw new Error("Please select an app");

  const usersQuery = query(
    collection(firestore, "users"),
    where("email", "==", normalizedEmail),
  );
  const usersSnapshot = await getDocs(usersQuery);
  if (usersSnapshot.empty) {
    throw new Error("No registered user found with this email");
  }

  const userSnapshot = usersSnapshot.docs[0];
  const targetUser = userSnapshot.data();
  const userId = targetUser.uid || userSnapshot.id;
  const existingOrders = await getDocs(query(
    ordersCollection(),
    where("user_id", "==", userId),
    where("app_id", "==", app.app_id),
    where("status", "==", "approved"),
  ));
  if (!existingOrders.empty) {
    throw new Error("This user already has approved access to this app");
  }

  const approvedAt = new Date();
  const expiresAt = new Date(approvedAt);
  expiresAt.setFullYear(expiresAt.getFullYear() + 1);
  const gift = await addDoc(ordersCollection(), {
    orderId: "",
    app_id: app.app_id,
    app_title: app.title,
    user_id: userId,
    user_email: targetUser.email || normalizedEmail,
    user_name: targetUser.name || targetUser.email || normalizedEmail,
    payment_method: "admin_gift",
    amount_mmk: 0,
    screenshot_path: "",
    screenshot_url: "",
    status: "approved",
    source: "admin_gift",
    gifted: true,
    gifted_by: adminUser?.email || adminUser?.user_id || "admin",
    createdAt: Timestamp.fromDate(approvedAt),
    reviewed_at: approvedAt.toISOString(),
    approvedAt: Timestamp.fromDate(approvedAt),
    updatedAt: serverTimestamp(),
  });

  await updateDoc(doc(firestore, "orders", gift.id), { orderId: gift.id });
  await setDoc(doc(firestore, "users", userId), {
    premium: true,
    premium_expires_at: expiresAt.toISOString(),
    plan: "premium",
    subscriptionStatus: "active",
    premiumStart: Timestamp.fromDate(approvedAt),
    premiumExpiry: Timestamp.fromDate(expiresAt),
    maxDevices: 2,
    purchases: {
      [app.app_id]: {
        status: "active",
        order_id: gift.id,
        source: "admin_gift",
        approved_at: approvedAt.toISOString(),
        expires_at: expiresAt.toISOString(),
      },
    },
    updatedAt: serverTimestamp(),
  }, { merge: true });

  return gift.id;
}
